import { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight,
  ClipboardList,
  ExternalLink,
  GripVertical,
  UtensilsCrossed,
} from "lucide-react";
import { appConfig } from "@ir/config";
import {
  formatYesNo,
  getFastFoodStepFormPrefill,
} from "@ir/services";
import {
  Alert,
  Badge,
  Button,
  DetailList,
  EmptyState,
  PageHeader,
  useIsDesktop,
  type DetailItem,
} from "@ir/ui";
import {
  ADDITIONAL_DRIVER_FIELDS,
  ARRAY_SECTIONS,
  MAIN_SECTIONS,
} from "../features/step-forms/fastFoodFields";
import { useAsyncData } from "../hooks/useAsyncData";

type FormData = Record<string, unknown>;
type CapturedFormData = FormData & {
  __capturedFields?: unknown;
};

type CapturedArraySection = {
  section: string;
  key: string;
  rows: {
    index: number;
    items: DetailItem[];
  }[];
};

type CapturedSection = {
  id: string;
  title: string;
  items: DetailItem[];
  arrays: CapturedArraySection[];
};

interface NavigationMessage {
  path: string;
  state?: unknown;
}

interface QuoteFormMessage {
  source?: string;
  type?: string;
  detail?: FormData | NavigationMessage;
  data?: FormData;
  path?: string;
  state?: unknown;
}

const MIN_PANEL_WIDTH = 320;
const MAX_PANEL_FRACTION = 0.6;
const ISO_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

function formatDateForDisplay(value: string): string {
  if (!ISO_DATE_PATTERN.test(value)) return value;

  const [year, month, day] = value.split("-");
  return `${day}/${month}/${year}`;
}

function displayValue(value: unknown): string {
  if (value === null || value === undefined || value === "") return "";
  if (typeof value === "boolean") return formatYesNo(value);
  if (Array.isArray(value)) {
    return value
      .map((entry) => displayValue(entry))
      .filter(Boolean)
      .join(", ");
  }
  if (typeof value === "object") {
    return JSON.stringify(value);
  }

  const stringValue = String(value);
  return formatDateForDisplay(stringValue);
}

function isMeaningfulCapturedValue(value: unknown): boolean {
  if (value === "" || value === null || value === undefined) return false;
  if (Array.isArray(value)) return value.some(isMeaningfulCapturedValue);
  if (typeof value === "object") {
    return Object.values(value).some(isMeaningfulCapturedValue);
  }
  return true;
}

function getBooleanFlag(
  data: Record<string, unknown>,
  key: string,
): boolean {
  return data[key] === true || data[key] === "Yes";
}

function shouldShowFieldValue(
  formData: CapturedFormData,
  key: string,
  value: unknown,
): boolean {
  if (!isMeaningfulCapturedValue(value)) return false;

  if (key === "howVehicleIsModified") {
    return getBooleanFlag(formData, "vehicleModifications");
  }

  if (key === "kindOfMedicalCondition") {
    return getBooleanFlag(formData, "haveMedicalConditions");
  }

  return false;
}

function getCapturedFieldSet(formData: CapturedFormData): Set<string> {
  const fields = formData.__capturedFields;
  if (Array.isArray(fields)) {
    return new Set(
      fields.filter((field): field is string => typeof field === "string"),
    );
  }

  return new Set(
    Object.entries(formData)
      .filter(
        ([key, value]) =>
          !key.startsWith("__") &&
          typeof value !== "object" &&
          isMeaningfulCapturedValue(value),
      )
      .map(([key]) => key),
  );
}

function isCapturedField(capturedFields: Set<string>, key: string): boolean {
  return (
    capturedFields.has(key) ||
    [...capturedFields].some((field) => field.startsWith(`${key}.`))
  );
}

function isCapturedPath(capturedFields: Set<string>, path: string): boolean {
  return (
    capturedFields.has(path) ||
    [...capturedFields].some(
      (field) => field.startsWith(`${path}.`) || path.startsWith(`${field}.`),
    )
  );
}

function isCapturedArrayField(
  capturedFields: Set<string>,
  arrayKey: string,
  index: number,
  key: string,
): boolean {
  return (
    capturedFields.has(arrayKey) ||
    isCapturedPath(capturedFields, arrayKey) ||
    capturedFields.has(`${arrayKey}.${index}.${key}`) ||
    capturedFields.has(`${arrayKey}.${index}`) ||
    [...capturedFields].some((field) =>
      field.startsWith(`${arrayKey}.${index}.${key}.`),
    )
  );
}

function hasCapturedData(formData: CapturedFormData): boolean {
  const capturedFields = getCapturedFieldSet(formData);

  return [...capturedFields].some((field) => {
    if (field.startsWith("__")) return false;
    const rootKey = field.split(".")[0];
    return isMeaningfulCapturedValue(formData[rootKey]);
  });
}

function toSectionId(section: string): string {
  return section.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
}

function arraySectionsForMainSection(section: string) {
  if (section === "Driver") {
    return ARRAY_SECTIONS.filter((arraySection) => arraySection.key === "driverOccupations");
  }

  if (section === "History") {
    return ARRAY_SECTIONS.filter((arraySection) =>
      ["motorClaims", "motoringConvictionEntries", "nonMotoringConvictions"].includes(
        arraySection.key,
      ),
    );
  }

  return [];
}

function buildCapturedArraySection(
  capturedFields: Set<string>,
  arraySection: (typeof ARRAY_SECTIONS)[number],
  rows: Record<string, unknown>[],
  arrayKey = arraySection.key,
  includeMeaningfulValues = false,
): CapturedArraySection | null {
  const rowItems = rows
    .map((row, index) => ({
      index,
      items: arraySection.fields
        .filter((field) =>
          isCapturedArrayField(capturedFields, arrayKey, index, field.key) ||
          (includeMeaningfulValues &&
            isMeaningfulCapturedValue(row[field.key])),
        )
        .map((field) => ({
          label: field.label,
          value: displayValue(row[field.key]),
        }))
        .filter((item) => item.value !== ""),
    }))
    .filter((entry) => entry.items.length > 0);

  if (rowItems.length === 0) return null;

  return {
    section: arraySection.section,
    key: arrayKey,
    rows: rowItems,
  };
}

function shouldShowArraySection(
  formData: Record<string, unknown>,
  arrayKey: string,
): boolean {
  if (arrayKey === "driverOccupations") {
    return getBooleanFlag(formData, "secondaryOccupation");
  }

  if (arrayKey === "motoringConvictionEntries") {
    return getBooleanFlag(formData, "motoringConvictions");
  }

  if (arrayKey === "nonMotoringConvictions") {
    return getBooleanFlag(formData, "unspentCriminalConvictionsNonMotoring");
  }

  if (arrayKey === "motorClaims") {
    return getBooleanFlag(formData, "isMotorClaims");
  }

  return false;
}

/**
 * Agent-assisted quote entry.
 *
 * The WordPress step form runs in an iframe and posts its state out as the
 * agent types; the left panel mirrors it so the agent can read back what has
 * been captured without scrolling the form. Messages are only accepted from the
 * configured form origin.
 */
export function FastFoodStepFormPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sourceQuoteId = searchParams.get("sourceQuoteId")?.trim() || "";
  const iframeUrl = useMemo(() => buildAdminStepFormUrl(appConfig.fastFoodFormUrl), []);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const panelContainerRef = useRef<HTMLDivElement>(null);
  const isDesktop = useIsDesktop();

  const [formData, setFormData] = useState<FormData>({});
  const [navigation, setNavigation] = useState<NavigationMessage | null>(null);
  const [panelWidth, setPanelWidth] = useState(420);
  const [dragging, setDragging] = useState(false);
  const [iframeInstance, setIframeInstance] = useState(0);

  const allowedOrigin = (() => {
    try {
      return new URL(iframeUrl).origin;
    } catch {
      return null;
    }
  })();

  const prefill = useAsyncData(
    (signal) =>
      sourceQuoteId
        ? getFastFoodStepFormPrefill(sourceQuoteId, signal)
        : Promise.resolve({}),
    [sourceQuoteId],
  );
  const desktopCapturedIdPrefix = useId();
  const mobileCapturedIdPrefix = useId();

  const postPrefill = useCallback(() => {
    if (!sourceQuoteId || !prefill.data || !iframeRef.current?.contentWindow) {
      return;
    }

    iframeRef.current.contentWindow.postMessage(
      {
        source: "irev-admin-dashboard",
        type: "FORM_PREFILL",
        detail: prefill.data,
      },
      allowedOrigin ?? "*",
    );
  }, [allowedOrigin, prefill.data, sourceQuoteId]);

  useEffect(() => {
    postPrefill();
  }, [postPrefill]);

  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      // The iframe is third-party content; only trust the configured origin.
      if (allowedOrigin && event.origin !== allowedOrigin) return;

      const data = event.data as QuoteFormMessage;
      if (data?.source && data.source !== "irev-quote-form") return;

      if (data?.type === "FORM_UPDATE") {
        const update = (data.detail ?? data.data) as FormData | undefined;
        if (update) setFormData(update);
      } else if (data?.type === "FORM_RESET") {
        setFormData({});
        setNavigation(null);
      } else if (data?.type === "FORM_PREFILLED") {
        setNavigation(null);
      } else if (data?.type === "NAVIGATION") {
        const navigationDetail = data.detail as NavigationMessage | undefined;
        const path = navigationDetail?.path ?? data.path;
        if (typeof path === "string") {
          setNavigation({ path, state: navigationDetail?.state ?? data.state });
        }
      }
    };

    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [allowedOrigin]);

  const onPointerMove = useCallback((event: PointerEvent) => {
    const container = panelContainerRef.current;
    if (!container) return;

    const bounds = container.getBoundingClientRect();
    const max = bounds.width * MAX_PANEL_FRACTION;
    setPanelWidth(
      Math.min(max, Math.max(MIN_PANEL_WIDTH, event.clientX - bounds.left)),
    );
  }, []);

  const stopDragging = useCallback(() => {
    setDragging(false);
    window.removeEventListener("pointermove", onPointerMove);
    window.removeEventListener("pointerup", stopDragging);
  }, [onPointerMove]);

  const startDragging = useCallback(() => {
    setDragging(true);
    window.addEventListener("pointermove", onPointerMove);
    window.addEventListener("pointerup", stopDragging);
  }, [onPointerMove, stopDragging]);

  useEffect(() => stopDragging, [stopDragging]);

  const startBlankForm = () => {
    setFormData({});
    setNavigation(null);
    navigate("/step-forms/fast-food", { replace: true });
    setIframeInstance((current) => current + 1);
  };

  const hasData = hasCapturedData(formData);
  const desktopCapturedSections = useMemo(
    () =>
      hasData
        ? buildCapturedSections(formData as CapturedFormData, desktopCapturedIdPrefix)
        : [],
    [desktopCapturedIdPrefix, formData, hasData],
  );
  const mobileCapturedSections = useMemo(
    () =>
      hasData
        ? buildCapturedSections(formData as CapturedFormData, mobileCapturedIdPrefix)
        : [],
    [formData, hasData, mobileCapturedIdPrefix],
  );

  if (!iframeUrl) {
    return (
      <>
        <PageHeader icon={UtensilsCrossed} title="Fast food step form" />
        <Alert tone="danger" title="The step form is not configured">
          Set <code className="font-mono">VITE_FAST_FOOD_URL</code> for this
          environment.
        </Alert>
      </>
    );
  }

  return (
    <>
      <PageHeader
        icon={UtensilsCrossed}
        title={sourceQuoteId ? "Edit fast food submission" : "Fast food step form"}
        description="Complete a quote with a customer on the phone. Everything they tell you appears on the left as you type."
        actions={
          <>
            <Button variant="secondary" onClick={startBlankForm}>
              Start new form
            </Button>
            {navigation?.path && (
              <Button
                icon={ArrowRight}
                onClick={() => window.open(navigation.path, "_blank", "noopener,noreferrer")}
              >
                Continue to quotes
              </Button>
            )}
            <a href={iframeUrl} target="_blank" rel="noopener noreferrer">
              <Button variant="ghost" icon={ExternalLink}>
                Open form
              </Button>
            </a>
          </>
        }
      />

      {sourceQuoteId && (
        <Alert
          tone={prefill.error ? "danger" : "info"}
          className="mb-4"
          title={
            prefill.error
              ? "Could not load the original submission"
              : "Editing from an existing submission"
          }
        >
          {prefill.error
            ? prefill.error
            : prefill.loading
              ? "Loading the original customer details into the form."
              : "The form has been prefilled from the stored submission. Submitting it will create a new quote record."}
        </Alert>
      )}

      {navigation?.path && (
        <Alert tone="success" className="mb-4" title="Submission complete">
          The quote has been submitted. Use “Continue to quotes” to open the
          customer's quote screen in a new tab.
        </Alert>
      )}

      <div
        ref={panelContainerRef}
        className="flex h-[calc(100dvh-16rem)] min-h-[32rem] overflow-hidden rounded-xl border border-line bg-surface"
      >
        {/* Captured data */}
        <div
          style={isDesktop ? { width: panelWidth } : undefined}
          className="hidden min-w-0 shrink-0 flex-col border-r border-line bg-surface-sunken lg:flex"
        >
          <header className="border-b border-line bg-surface-sunken">
            <div className="flex items-center gap-2 px-4 py-3">
              <ClipboardList size={15} aria-hidden="true" className="text-gold-600" />
              <h2 className="text-sm font-semibold text-content">Captured so far</h2>
              {hasData && (
                <Badge tone="success" dot className="ml-auto">
                  Live
                </Badge>
              )}
            </div>
            {hasData && <CapturedSectionNav sections={desktopCapturedSections} />}
          </header>

          <div className="min-h-0 flex-1 overflow-y-auto px-4 py-3">
            {hasData ? (
              <CapturedData sections={desktopCapturedSections} />
            ) : (
              <EmptyState
                icon={ClipboardList}
                title="Nothing captured yet"
                description="Start filling in the form and the answers will mirror here."
              />
            )}
          </div>
        </div>

        {/* Drag handle */}
        <button
          type="button"
          aria-label="Resize the captured data panel"
          onPointerDown={startDragging}
          className={`hidden w-1.5 shrink-0 cursor-col-resize items-center justify-center border-r border-line transition-colors lg:flex ${
            dragging ? "bg-gold-500" : "bg-surface-sunken hover:bg-gold-300"
          }`}
        >
          <GripVertical size={12} aria-hidden="true" className="text-content-subtle" />
        </button>

        {/* The form itself */}
        <div className="min-w-0 flex-1">
          <iframe
            key={iframeInstance}
            ref={iframeRef}
            src={iframeUrl}
            onLoad={postPrefill}
            title="Fast food quote step form"
            className="size-full border-0"
            // The form is a first-party WordPress page, but it is still a
            // separate origin: keep it from navigating this window.
            sandbox="allow-scripts allow-forms allow-same-origin allow-popups"
          />
        </div>
      </div>

      {/* Below lg the panel would squeeze the form, so it moves underneath. */}
      {hasData && (
        <motion.section
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mt-4 rounded-xl border border-line bg-surface p-4 lg:hidden"
        >
          <h2 className="mb-3 flex items-center gap-2 text-sm font-semibold text-content">
            <ClipboardList size={15} aria-hidden="true" className="text-gold-600" />
            Captured so far
          </h2>
          <CapturedSectionNav sections={mobileCapturedSections} />
          <CapturedData sections={mobileCapturedSections} />
        </motion.section>
      )}
    </>
  );
}

function buildAdminStepFormUrl(rawUrl: string): string {
  if (!rawUrl) return rawUrl;

  try {
    const url = new URL(rawUrl);
    if (!url.searchParams.has("queryParam")) {
      url.searchParams.set("queryParam", "adminStepForm");
    }
    return url.toString();
  } catch {
    return rawUrl;
  }
}

function buildCapturedSections(
  formData: CapturedFormData,
  sectionIdPrefix: string,
): CapturedSection[] {
  const capturedFields = getCapturedFieldSet(formData);
  const additionalDrivers =
    (formData.additionalDrivers as Record<string, unknown>[]) ?? [];
  const visibleAdditionalDrivers = additionalDrivers
    .map((driver, index) => ({
      driver,
      index,
      items: ADDITIONAL_DRIVER_FIELDS.filter((field) =>
        isCapturedArrayField(
          capturedFields,
          "additionalDrivers",
          index,
          field.key,
        ),
      )
        .map((field) => ({
          label: field.label,
          value: displayValue(driver[field.key]),
        }))
        .filter((item) => item.value !== ""),
      arrays: ARRAY_SECTIONS.map((arraySection) =>
        buildCapturedArraySection(
          capturedFields,
          arraySection,
          (driver[arraySection.key] as Record<string, unknown>[]) ?? [],
          `additionalDrivers.${index}.${arraySection.key}`,
          shouldShowArraySection(driver, arraySection.key),
        ),
      ).filter(
        (arraySection): arraySection is CapturedArraySection =>
          arraySection !== null,
        )
    }))
    .filter((entry) => entry.items.length > 0 || entry.arrays.length > 0);

  const sections: CapturedSection[] = MAIN_SECTIONS.map((section) => {
    const items = section.fields
      .filter((field) => {
        const value = formData[field.key];
        return (
          isCapturedField(capturedFields, field.key) ||
          shouldShowFieldValue(formData, field.key, value)
        );
      })
      .map((field) => ({
        label: field.label,
        value: displayValue(formData[field.key]),
      }))
      .filter((item) => item.value !== "");

    const arrays = arraySectionsForMainSection(section.section)
      .map((arraySection) =>
        buildCapturedArraySection(
          capturedFields,
          arraySection,
          (formData[arraySection.key] as Record<string, unknown>[]) ?? [],
          arraySection.key,
          shouldShowArraySection(formData, arraySection.key),
        ),
      )
      .filter(
        (arraySection): arraySection is CapturedArraySection =>
          arraySection !== null,
      );

    return {
      id: `${sectionIdPrefix}-${toSectionId(section.section)}`,
      title: section.section,
      items,
      arrays,
    };
  }).filter((section) => section.items.length > 0 || section.arrays.length > 0);

  if (
    visibleAdditionalDrivers.length > 0 &&
    isCapturedField(capturedFields, "additionalDrivers")
  ) {
    const coverSection = sections.find((section) => section.title === "Cover");
    const additionalDriverSection = {
      section: "Additional drivers",
      key: "additionalDrivers",
      rows: visibleAdditionalDrivers.map(({ index, items, arrays }) => ({
        index,
        items: [
          ...items,
          ...arrays.flatMap((arraySection) =>
            arraySection.rows.flatMap((row) =>
              row.items.map((item) => ({
                label: `${arraySection.section.replace(/s$/, "")} ${row.index + 1} - ${item.label}`,
                value: item.value,
              })),
            ),
          ),
        ],
      })),
    };

    if (coverSection) {
      coverSection.arrays.push(additionalDriverSection);
    } else {
      sections.push({
        id: `${sectionIdPrefix}-cover`,
        title: "Cover",
        items: [],
        arrays: [additionalDriverSection],
      });
    }
  }

  return sections;
}

function CapturedSectionNav({ sections }: { sections: CapturedSection[] }) {
  return (
    <nav
      aria-label="Captured form sections"
      className="flex gap-1 overflow-x-auto border-t border-line bg-surface-sunken px-4 py-2 shadow-sm"
    >
      {sections.map((section) => (
        <button
          key={section.id}
          type="button"
          onClick={() =>
            document.getElementById(section.id)?.scrollIntoView({
              behavior: "smooth",
              block: "start",
            })
          }
          className="shrink-0 rounded-full border border-line bg-surface px-2.5 py-1 text-[11px] font-semibold text-content-subtle transition hover:border-gold-500 hover:text-content"
        >
          {section.title}
        </button>
      ))}
    </nav>
  );
}

function CapturedData({ sections }: { sections: CapturedSection[] }) {
  return (
    <div className="space-y-3">
      {sections.map((section) => (
        <section
          key={section.id}
          id={section.id}
          className="scroll-mt-14 rounded-lg border border-line bg-surface/60 p-3"
        >
          <h3 className="mb-2 text-[13px] font-bold uppercase tracking-[0.08em] text-content">
            {section.title}
          </h3>

          {section.items.length > 0 && (
            <DetailList columns={1} items={section.items} />
          )}

          {section.arrays.length > 0 && (
            <div className={section.items.length > 0 ? "mt-3 space-y-3" : "space-y-3"}>
              {section.arrays.map((arraySection) => (
                <div key={arraySection.key} className="space-y-2">
                  <p className="text-xs font-semibold text-content">
                    {arraySection.section}
                  </p>
                  {arraySection.rows.map(({ index, items }) => (
                    <div
                      key={index}
                      className="rounded-lg border border-line bg-surface p-2.5"
                    >
                      <p className="mb-1 text-xs font-medium text-content-muted">
                        {arraySection.section.replace(/s$/, "")} {index + 1}
                      </p>
                      <DetailList columns={1} items={items} />
                    </div>
                  ))}
                </div>
              ))}
            </div>
          )}
        </section>
      ))}
    </div>
  );
}
